import { useState } from 'react';

import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CircularProgress,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useSession } from '@/shared/session';

/** Lets a multi-company employee choose the active tenant before entering the app. */
export function TenantSelectPage() {
  const { tenants, setActiveTenant } = useSession();
  const navigate = useNavigate();
  const [pendingId, setPendingId] = useState<string | null>(null);

  const choose = async (tenantId: string) => {
    setPendingId(tenantId);
    try {
      await setActiveTenant(tenantId);
      navigate('/', { replace: true });
    } finally {
      setPendingId(null);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        bgcolor: 'background.default',
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 480 }}>
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <Typography variant="h5" component="h1">
              Choose a company
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You belong to several companies. Pick the one to work in.
            </Typography>
          </Stack>

          <List disablePadding>
            {tenants.map((tenant) => {
              const busy = pendingId === tenant.id;
              return (
                <ListItemButton
                  key={tenant.id}
                  disabled={pendingId !== null}
                  onClick={() => void choose(tenant.id)}
                  sx={{ borderRadius: 2, mb: 0.5 }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <StorefrontRoundedIcon fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={tenant.name} secondary={tenant.id} />
                  {busy ? <CircularProgress size={18} /> : <ChevronRightRoundedIcon color="action" />}
                </ListItemButton>
              );
            })}
          </List>
        </CardContent>
      </Card>
    </Box>
  );
}
